import { useEffect, useRef, useState } from "react";
import { useLang } from "../OneHealthContext";
import { PVTB_TRIALS, PVTB_ISI_MIN_MS, PVTB_ISI_MAX_MS, PVTB_LAPSE_MS,
         PVTB_SEVERE_LAPSE_MS, attentionFlagThreshold } from "../config";

/* PVT-B — a brief, adapted Psychomotor Vigilance Task (attention / reaction).
   Self-contained (no SDK): after a random wait the target fills in and the
   worker taps as fast as they can. We record reaction times, count lapses
   (RT ≥ PVTB_LAPSE_MS) and false starts, and derive an illustrative attention
   band. Gated on the per-worker cognitiveRequired flag. */

const rand = (min, max) => Math.floor(min + Math.random() * (max - min));

export default function Pvtb({ onDone, trials = PVTB_TRIALS, age }) {
  const { tr } = useLang();
  const trialCount = Number.isFinite(Number(trials)) && Number(trials) > 0
    ? Math.floor(Number(trials))
    : PVTB_TRIALS;
  const [phase, setPhase] = useState("intro"); // intro · waiting · stimulus · feedback · done
  const [trial, setTrial] = useState(0);
  const [flash, setFlash] = useState("");      // feedback line (RT or "too soon")
  const [summary, setSummary] = useState(null);

  const rtsRef = useRef([]);
  const falseRef = useRef(0);
  const shownAtRef = useRef(0);
  const waitTimer = useRef(null);
  const fbTimer = useRef(null);

  useEffect(() => () => {
    if (waitTimer.current) clearTimeout(waitTimer.current);
    if (fbTimer.current) clearTimeout(fbTimer.current);
  }, []);

  const scheduleStimulus = () => {
    setPhase("waiting");
    setFlash("");
    waitTimer.current = setTimeout(() => {
      shownAtRef.current = performance.now();
      setPhase("stimulus");
    }, rand(PVTB_ISI_MIN_MS, PVTB_ISI_MAX_MS));
  };

  const start = () => {
    rtsRef.current = []; falseRef.current = 0; setTrial(0);
    scheduleStimulus();
  };

  const nextTrial = () => {
    const n = trial + 1;
    if (n >= trialCount) {
      finish();
    } else {
      setTrial(n);
      scheduleStimulus();
    }
  };

  const finish = () => {
    const rts = rtsRef.current;
    const mean = rts.length ? Math.round(rts.reduce((a, b) => a + b, 0) / rts.length) : 0;
    const lapses = rts.filter((r) => r >= PVTB_LAPSE_MS).length;
    const falseStarts = falseRef.current;
    let band = "ok";
    const flagThreshold = attentionFlagThreshold(age);
    const severe = rts.filter((r) => r >= PVTB_SEVERE_LAPSE_MS).length;
    if (mean > flagThreshold || severe >= 3) band = "slow";
    else if (mean > flagThreshold - 100 || severe >= 2) band = "fair";
    else if (mean > 0 && falseStarts === 0 && severe === 0) band = "sharp";
    const s = { mean, lapses, falseStarts, band, count: rts.length, rts: [...rts], flagThreshold };
    setSummary(s);
    setPhase("done");
    return s;
  };

  const onTap = () => {
    if (phase === "waiting") {
      // Tapped before the target appeared — false start; restart this trial's wait.
      if (waitTimer.current) clearTimeout(waitTimer.current);
      falseRef.current += 1;
      setFlash(tr("pvtb.tooSoon"));
      setPhase("feedback");
      fbTimer.current = setTimeout(scheduleStimulus, 900);
    } else if (phase === "stimulus") {
      const rt = Math.round(performance.now() - shownAtRef.current);
      rtsRef.current.push(rt);
      setFlash(`${rt} ${tr("pvtb.ms")}`);
      setPhase("feedback");
      fbTimer.current = setTimeout(nextTrial, 700);
    }
  };

  if (phase === "intro") {
    return (
      <div className="oh-view" style={{ justifyContent: "center", alignItems: "center", textAlign: "center" }}>
        <div className="oh-kicker">{tr("pvtb.kicker")}</div>
        <div style={{ height: 8 }} />
        <h2 className="oh-h2">{tr("pvtb.title")}</h2>
        <div style={{ height: 8 }} />
        <p className="oh-sub" style={{ maxWidth: 340 }}>{tr("pvtb.intro", { n: trialCount })}</p>
        <div className="oh-spacer" />
        <button className="oh-btn" style={{ maxWidth: 360 }} onClick={start}>{tr("pvtb.start")}</button>
      </div>
    );
  }

  if (phase === "done" && summary) {
    const label = summary.band === "sharp" ? tr("pvtb.resultSharp") : summary.band === "slow" ? tr("pvtb.resultSlow") : tr("pvtb.resultOk");
    return (
      <div className="oh-view" style={{ justifyContent: "center", alignItems: "center", textAlign: "center" }}>
        <div className={"oh-mark lg " + (summary.band === "slow" ? "caution" : "clear")} />
        <div style={{ height: 20 }} />
        <h2 className="oh-h2">{tr("pvtb.complete")}</h2>
        <div style={{ height: 6 }} />
        <p className="oh-sub">{label}</p>
        <div className="oh-pvt-stats">
          <div className="oh-pvt-stat">
            <div className="oh-pvt-num oh-mono">{summary.mean}<small>{tr("pvtb.ms")}</small></div>
            <div className="oh-pvt-lbl">{tr("pvtb.meanRt")}</div>
          </div>
          <div className="oh-pvt-stat">
            <div className="oh-pvt-num oh-mono">{summary.lapses}</div>
            <div className="oh-pvt-lbl">{tr("pvtb.lapses")}</div>
          </div>
        </div>
        <div className="oh-spacer" />
        <button className="oh-btn" style={{ maxWidth: 360 }} onClick={() => onDone(summary)}>{tr("common.continue")}</button>
      </div>
    );
  }

  // running: waiting · stimulus · feedback
  const live = phase === "stimulus";
  const feedback = phase === "feedback";
  return (
    <div className="oh-view">
      <div className="oh-kicker">{tr("pvtb.kicker")}</div>
      <div style={{ height: 6 }} />
      <div className="oh-pvt-round">{tr("pvtb.round", { a: trial + 1, b: trialCount })}</div>
      <button
        type="button"
        className={"oh-pvt-pad" + (live ? " live" : "") + (feedback ? " fb" : "")}
        onClick={onTap}
      >
        <div className={"oh-pvt-target" + (live ? " on" : "")}>
          {live ? tr("pvtb.tapNow") : feedback ? flash : tr("pvtb.wait")}
        </div>
      </button>
      <p className="oh-sub" style={{ textAlign: "center" }}>{tr("pvtb.tap")}</p>
    </div>
  );
}
